import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const SOURCES = ["Website", "Referral", "LinkedIn", "Cold Outreach", "Event", "Other"];
const STAGES = ["Lead", "Qualified", "Proposal", "Won", "Lost"];
const EMPTY = {
  company_name: "", contact_name: "", contact_email: "", contact_phone: "",
  source: "Website", stage: "Lead", owner_id: "", expected_value: "", notes: "", next_follow_up_date: "",
};

export default function LeadFormDialog({ open, onOpenChange, lead, employees, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [currentLead, setCurrentLead] = useState(lead);
  const [followUpDate, setFollowUpDate] = useState("");
  const [followUpNote, setFollowUpNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [addingFollowUp, setAddingFollowUp] = useState(false);
  const isEdit = !!lead;

  useEffect(() => {
    if (open) {
      setForm(
        lead
          ? {
              company_name: lead.company_name, contact_name: lead.contact_name || "", contact_email: lead.contact_email || "",
              contact_phone: lead.contact_phone || "", source: lead.source, stage: lead.stage, owner_id: lead.owner_id || "",
              expected_value: lead.expected_value || "", notes: lead.notes || "", next_follow_up_date: lead.next_follow_up_date || "",
            }
          : EMPTY
      );
      setCurrentLead(lead);
      setFollowUpDate("");
      setFollowUpNote("");
    }
  }, [open, lead]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async () => {
    if (!form.company_name.trim()) {
      toast.error("Company name is required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, expected_value: parseFloat(form.expected_value) || 0, owner_id: form.owner_id || null };
      if (isEdit) {
        await api.put(`/leads/${lead.id}`, payload);
        toast.success("Lead updated");
      } else {
        await api.post("/leads", payload);
        toast.success("Lead added to pipeline");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const addFollowUp = async () => {
    if (!followUpDate || !followUpNote.trim()) {
      toast.error("Follow-up date and note are required");
      return;
    }
    setAddingFollowUp(true);
    try {
      const res = await api.post(`/leads/${lead.id}/follow-ups`, { date: followUpDate, note: followUpNote.trim() });
      setCurrentLead(res.data);
      toast.success("Follow-up logged");
      setFollowUpDate("");
      setFollowUpNote("");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAddingFollowUp(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lead-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit Lead" : "Add Lead"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Company / Lead Name</Label>
            <Input data-testid="lead-form-company-input" value={form.company_name} onChange={set("company_name")} placeholder="e.g. Nimbus Textiles Pvt Ltd" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Contact Name</Label>
              <Input data-testid="lead-form-contact-name-input" value={form.contact_name} onChange={set("contact_name")} />
            </div>
            <div className="space-y-1.5">
              <Label>Contact Email</Label>
              <Input data-testid="lead-form-contact-email-input" value={form.contact_email} onChange={set("contact_email")} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Contact Phone</Label>
              <Input data-testid="lead-form-contact-phone-input" value={form.contact_phone} onChange={set("contact_phone")} />
            </div>
            <div className="space-y-1.5">
              <Label>Expected Value (₹)</Label>
              <Input data-testid="lead-form-value-input" type="number" value={form.expected_value} onChange={set("expected_value")} placeholder="e.g. 250000" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Source</Label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger data-testid="lead-form-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Stage</Label>
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger data-testid="lead-form-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Owner</Label>
              <Select value={form.owner_id} onValueChange={(v) => setForm({ ...form, owner_id: v })}>
                <SelectTrigger data-testid="lead-form-owner-select"><SelectValue placeholder="Select owner" /></SelectTrigger>
                <SelectContent>
                  {(employees || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Next Follow-up Date</Label>
              <Input data-testid="lead-form-followup-date-input" type="date" value={form.next_follow_up_date} onChange={set("next_follow_up_date")} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea data-testid="lead-form-notes-input" value={form.notes} onChange={set("notes")} rows={2} />
          </div>

          {isEdit && (
            <div className="space-y-2 border-t border-[#E2E8F0] pt-3">
              <Label>Follow-up History</Label>
              <div className="max-h-32 overflow-y-auto space-y-1.5" data-testid="lead-followup-history">
                {(currentLead?.follow_ups || []).length === 0 && <p className="text-xs text-[#4B5563]">No follow-ups logged yet.</p>}
                {(currentLead?.follow_ups || []).slice().reverse().map((f, i) => (
                  <div key={i} className="text-xs bg-[#F8F9FA] rounded-md p-2">
                    <span className="font-medium text-[#1C1C1E]">{f.date}</span> · {f.note} <span className="text-[#4B5563]">— {f.created_by}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input data-testid="lead-form-new-followup-date" type="date" value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)} className="w-36" />
                <Input data-testid="lead-form-new-followup-note" value={followUpNote} onChange={(e) => setFollowUpNote(e.target.value)} placeholder="Follow-up note" />
                <Button size="sm" variant="outline" disabled={addingFollowUp} onClick={addFollowUp} data-testid="lead-form-add-followup-button">Log</Button>
              </div>
            </div>
          )}
          {currentLead?.converted_client_id && (
            <p className="text-xs text-[#00823B] bg-[#F7FEE7] rounded-md p-2" data-testid="lead-converted-notice">
              This lead has been converted to a Client record.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="lead-form-cancel-button">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="lead-form-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            {submitting ? "Saving..." : isEdit ? "Save Changes" : "Add Lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
